import React from 'react';
import { TrendingUp, Zap, ShieldCheck, Table2, HardDrive } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { formatFileSize } from '../../../utils/fileUrl';

interface AnalyticsKpiGridProps {
  totalSubmissions: number;
  validSubmissions: number;
  spamSubmissions: number;
  latencyP50: number;
  latencyP95: number;
  latencyP99: number;
  tablesCount: number;
  dynamicCount: number;
  strictCount: number;
  storageBytes?: number;
  filesCount?: number;
  displaySpamRate: string;
  displayCleanRate: string;
}

export const AnalyticsKpiGrid: React.FC<AnalyticsKpiGridProps> = ({
  totalSubmissions,
  validSubmissions,
  spamSubmissions,
  latencyP50,
  latencyP95,
  latencyP99,
  tablesCount,
  dynamicCount,
  strictCount,
  storageBytes = 0,
  filesCount = 0,
  displaySpamRate,
  displayCleanRate,
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
      {/* Card 1: Ingestion Volume */}
      <Card className="p-4 bg-cf-surface border-cf-border flex flex-col justify-between shadow-xs">
        <div className="flex items-center justify-between text-cf-textSecondary text-xs">
          <span className="font-medium">Total Ingested</span>
          <TrendingUp size={14} className="text-emerald-500 dark:text-emerald-400" />
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          <span className="text-2xl font-bold text-cf-textPrimary tabular-nums">
            {totalSubmissions.toLocaleString()}
          </span>
          <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
            {totalSubmissions > 0 ? '+100%' : '0%'}
          </span>
        </div>
        <div className="mt-3 flex items-center justify-between text-xs text-cf-textSecondary pt-2 border-t border-cf-border">
          <span>Valid: {validSubmissions.toLocaleString()}</span>
          <span className="text-rose-600 dark:text-rose-400 font-medium">
            Spam: {spamSubmissions.toLocaleString()}
          </span>
        </div>
      </Card>

      {/* Card 2: Ingestion Latency Percentiles */}
      <Card className="p-4 bg-cf-surface border-cf-border flex flex-col justify-between shadow-xs">
        <div className="flex items-center justify-between text-cf-textSecondary text-xs">
          <span className="font-medium">Ingestion Latency</span>
          <Zap size={14} className="text-cf-orange" />
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
            {latencyP50 > 0 ? Number(latencyP50).toFixed(2) : '0.00'} ms
          </span>
          <span className="text-xs bg-emerald-500/10 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 rounded font-semibold">
            p50
          </span>
        </div>
        <div className="mt-3 flex items-center justify-between text-xs text-cf-textSecondary pt-2 border-t border-cf-border">
          <span>
            p95: <strong className="text-cf-textPrimary">{latencyP95 > 0 ? Number(latencyP95).toFixed(2) : '0.00'}ms</strong>
          </span>
          <span>
            p99: <strong className="text-cf-textPrimary">{latencyP99 > 0 ? Number(latencyP99).toFixed(2) : '0.00'}ms</strong>
          </span>
        </div>
      </Card>

      {/* Card 3: Honeypot Quarantine */}
      <Card className="p-4 bg-cf-surface border-cf-border flex flex-col justify-between shadow-xs">
        <div className="flex items-center justify-between text-cf-textSecondary text-xs">
          <span className="font-medium">Spam Quarantine</span>
          <ShieldCheck size={14} className="text-emerald-500 dark:text-emerald-400" />
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          <span className="text-2xl font-bold text-cf-textPrimary tabular-nums">
            {displaySpamRate}
          </span>
        </div>
        <div className="mt-3 text-xs text-cf-textSecondary pt-2 border-t border-cf-border flex items-center justify-between">
          <span>Clean: {displayCleanRate}</span>
        </div>
      </Card>

      {/* Card 4: Active Ingress Endpoints */}
      <Card className="p-4 bg-cf-surface border-cf-border flex flex-col justify-between shadow-xs">
        <div className="flex items-center justify-between text-cf-textSecondary text-xs">
          <span className="font-medium">Active Ingress Endpoints</span>
          <Table2 size={14} className="text-cf-orange" />
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          <span className="text-2xl font-bold text-cf-textPrimary tabular-nums">
            {tablesCount}
          </span>
          <span className="text-xs text-cf-textSecondary font-medium">
            {tablesCount === 1 ? 'Datastore' : 'Datastores'}
          </span>
        </div>
        <div className="mt-3 flex items-center justify-between text-xs text-cf-textSecondary pt-2 border-t border-cf-border">
          <span>
            Dynamic: <strong className="text-cf-textPrimary font-semibold">{dynamicCount}</strong>
          </span>
          <span>
            Strict: <strong className="text-cf-textPrimary font-semibold">{strictCount}</strong>
          </span>
        </div>
      </Card>

      {/* Card 5: Project Storage */}
      <Card className="p-4 bg-cf-surface border-cf-border flex flex-col justify-between shadow-xs">
        <div className="flex items-center justify-between text-cf-textSecondary text-xs">
          <span className="font-medium">Project Storage</span>
          <HardDrive size={14} className="text-emerald-500 dark:text-emerald-400" />
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          <span className="text-2xl font-bold text-cf-textPrimary tabular-nums">
            {formatFileSize(storageBytes)}
          </span>
        </div>
        <div className="mt-3 flex items-center justify-between text-xs text-cf-textSecondary pt-2 border-t border-cf-border">
          <span>
            Files: <strong className="text-cf-textPrimary font-semibold">{filesCount}</strong>
          </span>
          <span>Stored Files</span>
        </div>
      </Card>
    </div>
  );
};
